import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { createWidget } from '@typeform/embed';
import '@typeform/embed/build/css/widget.css';
import { useSurveyContext } from '@/contexts/SurveyContext';
import { BORDER_RADIUS } from '@/theme/tokens';

const TYPEFORM_FORM_ID = 'bWNbgCWe';
const SURVEY_VERSION = 'v2.0';

export const CHIP_COLOR = '#617080';

const PANEL_WIDTH = 380;
const PANEL_HEIGHT = 520;
const EDGE_MARGIN = 12;
const SIDEBAR_WIDTH = 35;

const FeedbackSurveyChip: React.FC = () => {
  const { t } = useTranslation('ballot');
  const { sessionId, markSurveyCompleted, panelOpen, closeSurveyPanel } = useSurveyContext();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const widgetContainerRef = useRef<HTMLDivElement>(null);
  const widgetMountedRef = useRef(false);
  const widgetInstanceRef = useRef<{ unmount: () => void } | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Drag state
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [hasBeenDragged, setHasBeenDragged] = useState(false);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    startPosX: number;
    startPosY: number;
    dragging: boolean;
    moved: boolean;
  } | null>(null);

  // Compute default bottom-right of the full window, accounting for sidebar
  const getDefaultPosition = useCallback(() => {
    return {
      x: window.innerWidth - PANEL_WIDTH - EDGE_MARGIN - SIDEBAR_WIDTH,
      y: window.innerHeight - PANEL_HEIGHT - EDGE_MARGIN,
    };
  }, []);

  const handleClosePanel = useCallback(() => {
    widgetInstanceRef.current?.unmount();
    widgetInstanceRef.current = null;
    widgetMountedRef.current = false;
    closeSurveyPanel();
  }, [closeSurveyPanel]);

  const handleDialogKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  // Mount the Typeform widget when the panel opens (both mobile and desktop)
  useEffect(() => {
    if (!panelOpen || !widgetContainerRef.current || widgetMountedRef.current) return;

    widgetMountedRef.current = true;
    widgetInstanceRef.current = createWidget(TYPEFORM_FORM_ID, {
      container: widgetContainerRef.current,
      hidden: {
        session_id: sessionId,
        survey_version: SURVEY_VERSION,
      },
      onSubmit: () => {
        markSurveyCompleted();
        setTimeout(() => handleClosePanel(), 3000);
      },
    });
    return () => {
      widgetInstanceRef.current?.unmount();
      widgetInstanceRef.current = null;
      widgetMountedRef.current = false;
    };
  }, [panelOpen, sessionId, markSurveyCompleted, handleClosePanel]);

  // Move focus into the dialog when it opens
  useEffect(() => {
    if (panelOpen && dialogRef.current) {
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable[0]) focusable[0].focus();
    }
  }, [panelOpen]);

  // Reset position when panel opens
  useEffect(() => {
    if (panelOpen && !hasBeenDragged) {
      if (isMobile) {
        const mobileW = Math.min(PANEL_WIDTH, 340);
        const mobileH = Math.min(PANEL_HEIGHT, 460);
        setPosition({
          x: Math.round((window.innerWidth - mobileW) / 2),
          y: Math.round((window.innerHeight - mobileH) / 2),
        });
      } else {
        setPosition(getDefaultPosition());
      }
    }
  }, [panelOpen, hasBeenDragged, getDefaultPosition, isMobile]);

  // Drag handlers
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('button')) return;

      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startPosX: position.x,
        startPosY: position.y,
        dragging: true,
        moved: false,
      };
    },
    [position]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragRef.current?.dragging) return;
      e.preventDefault();

      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;

      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        dragRef.current.moved = true;
      }

      const effectivePanelWidth = isMobile ? Math.min(PANEL_WIDTH, 340) : PANEL_WIDTH;
      const effectivePanelHeight = isMobile ? Math.min(PANEL_HEIGHT, 460) : PANEL_HEIGHT;

      const newX = Math.max(
        EDGE_MARGIN,
        Math.min(
          window.innerWidth - effectivePanelWidth - EDGE_MARGIN,
          dragRef.current.startPosX + dx
        )
      );
      const newY = Math.max(
        EDGE_MARGIN,
        Math.min(
          window.innerHeight - effectivePanelHeight - EDGE_MARGIN,
          dragRef.current.startPosY + dy
        )
      );

      setPosition({ x: newX, y: newY });
    },
    [isMobile]
  );

  const handlePointerUp = useCallback(() => {
    if (dragRef.current?.moved) {
      setHasBeenDragged(true);
    }
    dragRef.current = null;
  }, []);

  return (
    <>
      {/* Floating draggable survey panel — opened from explicit survey buttons */}
      {panelOpen && (
        <Box
          ref={dialogRef}
          role="dialog"
          aria-label={t('feedbackSurvey.dialogAriaLabel')}
          aria-modal="true"
          onKeyDown={handleDialogKeyDown}
          sx={{
            position: 'fixed',
            left: position.x,
            top: position.y,
            width: { xs: Math.min(PANEL_WIDTH, 340), md: PANEL_WIDTH },
            height: { xs: Math.min(PANEL_HEIGHT, 460), md: PANEL_HEIGHT },
            zIndex: 1900,
            borderRadius: BORDER_RADIUS,
            overflow: 'hidden',
            boxShadow: '0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
            border: '1px solid #e2e8f0',
            backgroundColor: '#ffffff',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Draggable header */}
          <Box
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: '16px',
              py: '10px',
              borderBottom: '1px solid #f1f5f9',
              backgroundColor: '#f8fafc',
              flexShrink: 0,
              cursor: 'grab',
              userSelect: 'none',
              '&:active': { cursor: 'grabbing' },
            }}
          >
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 600,
                color: '#334155',
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              {t('feedbackSurvey.title')}
            </Typography>
            <IconButton
              onClick={handleClosePanel}
              aria-label={t('feedbackSurvey.closeAriaLabel')}
              size="small"
              sx={{
                width: '24px',
                height: '24px',
                color: '#94a3b8',
                '&:hover': { color: '#475569', backgroundColor: '#f1f5f9' },
              }}
            >
              <XMarkIcon width={16} height={16} />
            </IconButton>
          </Box>

          {/* Typeform widget container — force iframe to stay within bounds */}
          <Box
            ref={widgetContainerRef}
            sx={{
              flex: 1,
              overflow: 'hidden',
              position: 'relative',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              '& iframe': {
                position: 'absolute !important',
                top: '-35px !important',
                left: '-10px !important',
                width: 'calc(100% + 20px) !important',
                height: 'calc(100% + 35px) !important',
                maxHeight: '100% !important',
                border: 'none !important',
              },
              '& > div': {
                position: 'absolute !important',
                top: '0 !important',
                left: '0 !important',
                width: '100% !important',
                height: '100% !important',
              },
            }}
          />
        </Box>
      )}
    </>
  );
};

export default FeedbackSurveyChip;
